import { useEffect, useRef, useState } from 'react';
import { supportThread } from './content.js';

/**
 * The message thread that plays over the support photograph.
 *
 * WHY THIS IS DOM AND NOT VIDEO.
 *
 * The brief was "a video of them texting". This renders the same beat without
 * a generated clip, and the trade is one-sided:
 *
 *   - the words stay editable, so changing the pitch is a copy edit rather
 *     than a re-render and another generation spend;
 *   - text is text, so it survives screen readers, find-in-page and
 *     translation, none of which can see inside an mp4;
 *   - bubbles composited at device resolution stay crisp on a 3x phone where
 *     an inlined clip is either soft or several megabytes;
 *   - `prefers-reduced-motion` can actually be honoured, which for a looping
 *     autoplaying video means "do not play it at all".
 *
 * EVERY MESSAGE IS IN THE DOM FROM FIRST PAINT. The sequence only animates
 * opacity and transform. That is deliberate: a screen reader gets the whole
 * exchange in order and at once, with no aria-live chatter, and a reader who
 * has asked for reduced motion gets the finished state rather than an empty
 * box. Staging is decoration layered on top of content that is already there.
 */

/**
 * Arrival times, in ms from the moment the photo enters view.
 *
 * Five beats now, not three: the two who complained answer HR back. The whole
 * exchange still lands inside about four seconds, because a visitor scrolling
 * past will not wait longer than that and the last line is the one worth
 * seeing. The pause before HR's reply is the longest gap on purpose — that is
 * the turn.
 */
const AT = [0, 900, 2400, 3300, 3900] as const;

/**
 * When the typing indicator appears, derived rather than written down.
 *
 * It belongs immediately before the official reply. Hard-coding a millisecond
 * value meant that adding a message to the thread silently detached the
 * indicator from the message it is meant to precede.
 */
const OFFICIAL_INDEX = supportThread.findIndex((m) => m.official);
const TYPING_IN = Math.max(0, (AT[OFFICIAL_INDEX] ?? 0) - 1000);

export function SupportThread() {
  const ref = useRef<HTMLDivElement>(null);
  const [staged, setStaged] = useState(false);
  const [arrived, setArrived] = useState(supportThread.length);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Reduced motion, or a browser without IntersectionObserver, keeps the
    // finished state that the markup already renders. Nothing to schedule.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) return;

    // Only now do we hide anything: `staged` is what switches the container
    // from "all messages shown" to "messages arrive in sequence". Setting it
    // here rather than in the initial state means the no-JS and reduced-motion
    // paths never pass through an empty frame.
    setStaged(true);
    setArrived(0);

    const timers: number[] = [];
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.disconnect();
        AT.forEach((delay, index) => {
          timers.push(window.setTimeout(() => setArrived(index + 1), delay));
        });
        timers.push(window.setTimeout(() => setTyping(true), TYPING_IN));
        timers.push(window.setTimeout(() => setTyping(false), AT[OFFICIAL_INDEX] ?? AT[AT.length - 1]));
      }
    }, { rootMargin: '0px 0px -15% 0px', threshold: 0.25 });

    observer.observe(node);
    return () => {
      observer.disconnect();
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  return (
    <div className="support__thread" ref={ref} data-staged={staged ? 'true' : 'false'}>
      <ol className="support__thread-list">
        {supportThread.map((message, index) => (
          <li
            key={message.from + index}
            className={`support__bubble${message.official ? ' support__bubble--official' : ''}`}
            data-arrived={index < arrived ? 'true' : 'false'}
            data-typing={message.official && typing && index >= arrived ? 'true' : 'false'}
          >
            <span className="support__bubble-from">{message.from}</span>
            <span className="support__bubble-text">{message.text}</span>
            {/*
              Decorative. The indicator only says "somebody is composing" — the
              message it precedes is in this same element and already in the
              accessibility tree, so announcing the dots would make a screen
              reader read the beat twice.

              It is positioned over the bubble rather than added after it so
              that the composing state costs no layout: every bubble holds its
              final size from first paint, which is what lets the messages fade
              in without the stack jumping around underneath them.
            */}
            {message.official ? (
              <span className="support__bubble-dots" aria-hidden="true">
                <span /><span /><span />
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}
